import type { DispatchedJob } from '@whittlelabs/jobs';
import {
  renderPromptExecution,
  readPromptExecutionSpec,
  PROMPT_EXECUTION_STRATEGY,
} from '@whittlelabs/loom';
import type { CostHint, ExecutorCapability, ExecutorResponse, JobExecutor } from './types';
import { assertOutputValid } from './validate-output';

/**
 * HTTP-API executor: POSTs the rendered prompt to an LLM endpoint. Auto-detects
 * Anthropic vs OpenAI response format from the URL. Interprets the
 * `prompt-execution` strategy off the opaque payload.
 */
interface HttpApiConfig {
  url: string;
  headers: Record<string, string>;
  model: string;
  maxTokens: number;
  timeout: number;
  capabilityId: string;
}

const DEFAULT_MAX_TOKENS = 4096;
const DEFAULT_TIMEOUT = 120_000;

class HttpApiExecutor implements JobExecutor {
  readonly capability: ExecutorCapability;
  readonly strategies = [PROMPT_EXECUTION_STRATEGY];
  private readonly config: HttpApiConfig;

  constructor(config: HttpApiConfig) {
    this.config = config;
    this.capability = { id: config.capabilityId };
  }

  estimate(dispatch: DispatchedJob): CostHint {
    const spec = readPromptExecutionSpec(dispatch.inputs);
    return {
      inputTokens: spec.costCapHint?.estimatedInputTokens,
      outputTokens: spec.costCapHint?.estimatedOutputTokens,
    };
  }

  async execute(dispatch: DispatchedJob, signal: AbortSignal): Promise<ExecutorResponse> {
    const { prompt, spec } = renderPromptExecution(dispatch.inputs);
    const outputLabel = spec.outputLabel;
    const isAnthropic = this.config.url.includes('anthropic');

    const body = {
      model: this.config.model,
      max_tokens: this.config.maxTokens,
      messages: [{ role: 'user', content: prompt }],
    };

    const timeoutController = new AbortController();
    const timer = setTimeout(() => timeoutController.abort(), this.config.timeout);

    function onAbort(): void {
      timeoutController.abort();
    }
    signal.addEventListener('abort', onAbort, { once: true });

    try {
      const response = await fetch(this.config.url, {
        method: 'POST',
        headers: {
          ...this.config.headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: timeoutController.signal,
      });

      if (!response.ok) {
        let bodyText = '';
        try {
          bodyText = await response.text();
        } catch {
          // ignore
        }
        throw new Error(`HTTP ${response.status}${bodyText ? `: ${bodyText}` : ''}`);
      }

      const data = (await response.json()) as Record<string, unknown>;
      let rawText: string;
      let usage: ExecutorResponse['usage'] | undefined;

      if (isAnthropic) {
        const content = data.content as Array<Record<string, unknown>> | undefined;
        rawText = (content?.[0]?.text as string) ?? '';
        const u = data.usage as { input_tokens?: number; output_tokens?: number } | undefined;
        if (u) {
          usage = {
            inputTokens: u.input_tokens,
            outputTokens: u.output_tokens,
            aiProvider: 'anthropic',
            aiModel: this.config.model,
          };
        }
      } else {
        const choices = data.choices as Array<Record<string, unknown>> | undefined;
        const message = choices?.[0]?.message as Record<string, unknown> | undefined;
        rawText = (message?.content as string) ?? '';
        const u = data.usage as { prompt_tokens?: number; completion_tokens?: number } | undefined;
        if (u) {
          usage = {
            inputTokens: u.prompt_tokens,
            outputTokens: u.completion_tokens,
            aiProvider: 'openai',
            aiModel: this.config.model,
          };
        }
      }

      const parsed = safeJsonParse(rawText);
      assertOutputValid(spec, parsed);
      return {
        outputs: [{ label: outputLabel, content: parsed ?? { raw: rawText } }],
        rawText,
        parsed,
        ...(usage ? { usage } : {}),
      };
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    }
  }
}

export function createHttpApiExecutor(
  config: Record<string, unknown>,
  capabilityId = 'http-api',
): HttpApiExecutor {
  const url = config.url;
  if (typeof url !== 'string' || url.length === 0) {
    throw new Error('HttpApiExecutor requires a non-empty "url" string in config');
  }

  const headers = config.headers;
  if (
    headers === undefined ||
    headers === null ||
    typeof headers !== 'object' ||
    Array.isArray(headers)
  ) {
    throw new Error('HttpApiExecutor requires a "headers" object in config');
  }
  for (const [key, value] of Object.entries(headers)) {
    if (typeof key !== 'string' || typeof value !== 'string') {
      throw new Error('All header keys and values must be strings');
    }
  }

  const model = config.model;
  if (typeof model !== 'string' || model.length === 0) {
    throw new Error('HttpApiExecutor requires a non-empty "model" string in config');
  }

  let maxTokens = DEFAULT_MAX_TOKENS;
  if (config.maxTokens !== undefined) {
    if (typeof config.maxTokens !== 'number' || config.maxTokens <= 0) {
      throw new Error('"maxTokens" must be a positive number');
    }
    maxTokens = config.maxTokens;
  }

  let timeout = DEFAULT_TIMEOUT;
  if (config.timeout !== undefined) {
    if (typeof config.timeout !== 'number' || config.timeout <= 0) {
      throw new Error('"timeout" must be a positive number');
    }
    timeout = config.timeout;
  }

  return new HttpApiExecutor({
    url,
    headers: headers as Record<string, string>,
    model,
    maxTokens,
    timeout,
    capabilityId,
  });
}

function safeJsonParse(s: string): Record<string, unknown> | undefined {
  try {
    const parsed = JSON.parse(s);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}
